import { useState, useMemo, useEffect, useRef } from "react"
import { useLocation } from "wouter"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { isValidTitle } from "@/lib/postTitle"
import { statusConfig } from "@/lib/status-config"
import { StatCard } from "@/components/StatCard"
import { useToast } from "@/hooks/use-toast"
import { useQuery } from "@tanstack/react-query"
import type { GeneratedPost } from "@shared/schema"
import { postModuleHref } from "@shared/postRoutes"
import { useEdition } from "@/lib/edition"
import { ApprovedPendingList, type ApprovedPendingPost } from "@/components/instagram/ApprovedPendingList"
import {
  CalendarDays,
  CalendarClock,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  ChevronRight,
} from "lucide-react"
import { FaLinkedin, FaFacebook, FaInstagram } from "react-icons/fa"
import { AnimatedSection } from "@/components/AnimatedSection"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { format, isToday, isTomorrow, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isWithinInterval } from "date-fns"
import { de } from "date-fns/locale"

const platformIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  linkedin: FaLinkedin,
  facebook: FaFacebook,
  instagram: FaInstagram,
}

const platformColors: Record<string, string> = {
  linkedin: "bg-blue-600",
  facebook: "bg-blue-500",
  instagram: "bg-pink-500",
}

const platformNames: Record<string, string> = {
  linkedin: "LinkedIn",
  facebook: "Facebook",
  instagram: "Instagram",
}

// Where each post source opens. Photo posts (contentUploadId) open the Post Vorschau modal in
// the Foto-Bibliothek; industry_news / repurposing_content / pillar_post open the EXACT post on
// their page; napkin/evergreen land on their content page (no single-item deep-link yet).
const SOURCE_ROUTES: Record<string, string> = {
  evergreen_content: "/evergreen-content",
  napkin_wisdom: "/napkin-wisdom",
}

interface PublishingPlanData {
  scheduled: GeneratedPost[]
  posted: GeneratedPost[]
  /** Hotel-Edition B1: approved in the weekly plan, not yet handed to the planner (hotels only). */
  approvedPending?: ApprovedPendingPost[]
}

function getDayLabel(date: Date): string {
  if (isToday(date)) return "Heute"
  if (isTomorrow(date)) return "Morgen"
  return format(date, "EEEE, d. MMMM", { locale: de })
}

function groupByDay(posts: GeneratedPost[], dateField: "scheduledFor" | "postedAt"): Record<string, GeneratedPost[]> {
  const groups: Record<string, GeneratedPost[]> = {}
  for (const post of posts) {
    const dateValue = post[dateField]
    if (!dateValue) continue
    const date = new Date(dateValue)
    const key = format(date, "yyyy-MM-dd")
    if (!groups[key]) groups[key] = []
    groups[key].push(post)
  }
  return groups
}

function PostCard({ post, kind, highlight, onOpen }: { post: GeneratedPost; kind: "scheduled" | "overdue" | "posted"; highlight?: boolean; onOpen?: (post: GeneratedPost) => void }) {
  const dateValue = kind === "posted" ? post.postedAt : post.scheduledFor
  const time = dateValue ? format(new Date(dateValue), "HH:mm") : ""
  const PlatformIcon = platformIcons[post.platform] || FaLinkedin
  const platformColor = platformColors[post.platform] || "bg-gray-500"
  const platformName = platformNames[post.platform] || post.platform
  const cfg = statusConfig[kind] || statusConfig.scheduled
  const StatusIcon = cfg.icon

  // First non-empty content line (markdown stripped) as preview.
  const preview = post.postContent
    ? post.postContent
        .replace(/\*\*([^*]+)\*\*/g, "$1")
        .replace(/__([^_]+)__/g, "$1")
        .split("\n")
        .find((line) => line.trim().length > 0) || ""
    : ""
  const truncated = preview.length > 80 ? preview.substring(0, 77) + "..." : preview
  // Don't show the preview when it just repeats the title (the generator often uses the first
  // content line AS the title) — otherwise the card prints the same sentence twice.
  const titleShown = isValidTitle(post.postTitle)
  const previewIsDupe = titleShown && !!post.postTitle && preview.trim().toLowerCase() === post.postTitle.trim().toLowerCase()
  const showPreview = truncated.length > 0 && !previewIsDupe

  return (
    <div
      id={`plan-post-${post.id}`}
      onClick={onOpen ? () => onOpen(post) : undefined}
      onKeyDown={onOpen ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(post) } } : undefined}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      title={onOpen ? "Post öffnen" : undefined}
      className={`flex items-start gap-3 py-3 group rounded-lg transition-colors -mx-2 px-2 ${
        onOpen ? "cursor-pointer hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-ring" : ""
      } ${
        highlight ? "bg-blue-50 dark:bg-blue-950/30 ring-1 ring-blue-300 dark:ring-blue-800" : ""
      }`}
    >
      {/* Time */}
      <div className="w-14 flex-shrink-0 text-right">
        <span className="text-sm font-mono font-medium text-muted-foreground">{time}</span>
      </div>

      {/* Timeline dot */}
      <div className="flex flex-col items-center flex-shrink-0">
        <div className={`w-8 h-8 rounded-full ${platformColor} flex items-center justify-center`}>
          <PlatformIcon className="h-3.5 w-3.5 text-white" />
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-sm font-medium">{platformName}</span>
          <Badge className={`text-xs gap-1 ${cfg.color}`} data-testid={`badge-plan-status-${post.id}`}>
            <StatusIcon className="h-2.5 w-2.5" />
            {cfg.label}
          </Badge>
        </div>
        {titleShown && (
          <p className="text-sm font-medium truncate">{post.postTitle}</p>
        )}
        {showPreview && (
          <p className="text-sm text-muted-foreground truncate">{truncated}</p>
        )}
      </div>

      {onOpen && (
        <ChevronRight className="h-4 w-4 self-center flex-shrink-0 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" aria-hidden="true" />
      )}
    </div>
  )
}

function DayGroups({ groups, order, kind, highlightId, onOpen }: {
  groups: Record<string, GeneratedPost[]>
  order: "asc" | "desc"
  kind: "scheduled" | "overdue" | "posted"
  highlightId: string | null
  onOpen: (post: GeneratedPost) => void
}) {
  const dateField = kind === "posted" ? "postedAt" : "scheduledFor"
  return (
    <div className="space-y-1">
      {Object.entries(groups)
        .sort(([a], [b]) => (order === "asc" ? a.localeCompare(b) : b.localeCompare(a)))
        .map(([dayKey, posts]) => (
          <div key={dayKey}>
            <div className="flex items-center gap-2 py-2">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {getDayLabel(new Date(dayKey + "T12:00:00"))}
              </span>
              <div className="h-px flex-1 bg-border" />
            </div>
            {posts
              .sort((a, b) => {
                const ta = new Date((a[dateField] as any)!).getTime()
                const tb = new Date((b[dateField] as any)!).getTime()
                return order === "asc" ? ta - tb : tb - ta
              })
              .map((post) => (
                <PostCard key={post.id} post={post} kind={kind} highlight={post.id === highlightId} onOpen={onOpen} />
              ))}
          </div>
        ))}
    </div>
  )
}

export default function Veroeffentlichungsplan() {
  const [view, setView] = useState<"week" | "month">("week")
  const highlightId = useMemo(() => new URLSearchParams(window.location.search).get("post"), [])
  const didScrollRef = useRef(false)
  const [, setLocation] = useLocation()
  const { toast } = useToast()
  const { edition } = useEdition()

  // Click a planned/published post → open it wherever it came from.
  const openPost = (post: GeneratedPost) => {
    const uploadId = (post as any).contentUploadId as string | null | undefined
    if (uploadId) {
      setLocation(`/foto-bibliothek?upload=${uploadId}`)
      return
    }
    const sourceType = (post as any).sourceType ?? ""
    const platformParam = post.platform ? `&platform=${post.platform}` : ""
    if (sourceType === "industry_news" && post.sourceId) {
      setLocation(`/industry-news?news=${post.sourceId}${platformParam}`)
      return
    }
    if (sourceType === "repurposing_content" && post.sourceId) {
      setLocation(`/repurposing?item=${post.sourceId}${platformParam}`)
      return
    }
    // Pillar posts open their exact post in the module of their edition (shared/postRoutes.ts; agencies:
    // the Pillar Posts page, which auto-opens the modal via ?post=).
    if (sourceType === "pillar_post" && post.sourceId) {
      setLocation(postModuleHref({ id: post.sourceId, platform: post.platform, mediaFormat: (post as any).mediaFormat }, edition))
      return
    }
    const route = SOURCE_ROUTES[sourceType]
    if (route) {
      setLocation(route)
      return
    }
    // Unknown source: don't strand the user on an unrelated page (was: silent redirect to Foto-Bibliothek).
    toast({
      title: "Keine Detailansicht",
      description: "Für diesen Beitrag gibt es keine verknüpfte Ansicht.",
      variant: "destructive",
    })
  }

  const { data, isLoading, error } = useQuery<PublishingPlanData>({
    // Array key so the broad ['/api/generated-posts'] invalidation (photo publish/schedule,
    // content library) prefix-matches this and the plan refreshes instead of going stale.
    queryKey: ["/api/generated-posts", "publishing-plan"],
    queryFn: async () => {
      const response = await fetch("/api/generated-posts/publishing-plan")
      if (!response.ok) throw new Error("Failed to fetch publishing plan")
      return response.json()
    },
    staleTime: 30000,
  })

  // The Woche/Monat toggle scopes the PUBLISHED history (stat + list) — history is naturally
  // time-bounded. The scheduled side (overdue backlog + upcoming pipeline) is ALWAYS shown in
  // full so nothing planned is ever hidden (there is no next-period navigation yet), which also
  // removes the confusing "week count > month count" the two nested windows used to produce.
  const { activeStart, activeEnd, overdue, upcoming, posted, overdueByDay, upcomingByDay, postedByDay, plannedCount, publishedCount, overdueCount } = useMemo(() => {
    const now = new Date()
    const activeStart = view === "week" ? startOfWeek(now, { weekStartsOn: 1 }) : startOfMonth(now)
    const activeEnd = view === "week" ? endOfWeek(now, { weekStartsOn: 1 }) : endOfMonth(now)
    const inWindow = (d: Date | string | null | undefined) => !!d && isWithinInterval(new Date(d), { start: activeStart, end: activeEnd })
    const isPast = (p: GeneratedPost) => !!p.scheduledFor && new Date(p.scheduledFor) < now

    const allScheduled = data?.scheduled?.filter((p) => p.scheduledFor) ?? []
    const allPosted = data?.posted?.filter((p) => p.postedAt) ?? []

    const overdue = allScheduled.filter(isPast)          // past + unpublished (e.g. scheduler failure)
    const upcoming = allScheduled.filter((p) => !isPast(p)) // full future pipeline, not window-scoped
    const posted = allPosted.filter((p) => inWindow(p.postedAt) || p.id === highlightId) // history, windowed (+ deep-link)

    return {
      activeStart, activeEnd, overdue, upcoming, posted,
      overdueByDay: groupByDay(overdue, "scheduledFor"),
      upcomingByDay: groupByDay(upcoming, "scheduledFor"),
      postedByDay: groupByDay(posted, "postedAt"),
      plannedCount: upcoming.length,
      publishedCount: allPosted.filter((p) => inWindow(p.postedAt)).length,
      overdueCount: overdue.length,
    }
  }, [data, view, highlightId])

  // Deep-link: ?post=<id> scrolls the target into view + highlights it. The target is always
  // rendered (overdue always shows; upcoming/posted include the highlighted id above).
  useEffect(() => {
    if (didScrollRef.current || !highlightId || !data) return
    didScrollRef.current = true
    setTimeout(() => {
      document.getElementById(`plan-post-${highlightId}`)?.scrollIntoView({ behavior: "smooth", block: "center" })
    }, 250)
  }, [highlightId, data])

  if (isLoading) {
    return (
      <div className="container mx-auto p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-28 rounded-xl" />
          ))}
        </div>
        <div className="space-y-4">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-56 rounded-xl" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="container mx-auto p-6">
        <Card className="border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-5 w-5" />
              <p>Fehler beim Laden des Veröffentlichungsplans.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const rangeHint = view === "week" ? "Diese Woche" : "Diesen Monat"
  const rangeLabel = view === "week"
    ? `${format(activeStart, "d. MMM", { locale: de })} bis ${format(activeEnd, "d. MMM yyyy", { locale: de })}`
    : format(activeStart, "MMMM yyyy", { locale: de })

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <AnimatedSection>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3">
              <CalendarDays className="h-8 w-8 text-blue-600" />
              Veröffentlichungsplan
            </h1>
            <p className="text-muted-foreground mt-2">
              Ihr Überblick über geplante und veröffentlichte Beiträge
            </p>
          </div>
          <Tabs value={view} onValueChange={(v) => setView(v as "week" | "month")}>
            <TabsList>
              <TabsTrigger value="week">Diese Woche</TabsTrigger>
              <TabsTrigger value="month">Dieser Monat</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </AnimatedSection>

      {/* Stats — scoped to the selected window */}
      <AnimatedSection delay={100}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <StatCard title="Geplant" value={plannedCount} hint="Anstehend" icon={CalendarClock} testId="stat-planned" />
          <StatCard title="Veröffentlicht" value={publishedCount} hint={rangeHint} icon={CheckCircle} testId="stat-published" />
          <StatCard title="Überfällig" value={overdueCount} hint="Nicht veröffentlicht" icon={AlertTriangle} testId="stat-overdue" />
        </div>
      </AnimatedSection>

      {/* Date range indicator */}
      <AnimatedSection delay={150}>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <CalendarDays className="h-4 w-4" />
          <span>{rangeLabel}</span>
        </div>
      </AnimatedSection>

      {/* Overdue posts — always shown so publish failures are visible */}
      {overdue.length > 0 && (
        <AnimatedSection delay={175}>
          <Card className="border-red-200 dark:border-red-900/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                Überfällige Posts
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DayGroups groups={overdueByDay} order="asc" kind="overdue" highlightId={highlightId} onOpen={openPost} />
            </CardContent>
          </Card>
        </AnimatedSection>
      )}

      {/* Hotel-Edition B1: approved, not yet handed to the planner — deletable while allowed */}
      {(data?.approvedPending?.length ?? 0) > 0 && (
        <AnimatedSection delay={190}>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Freigegeben, wird eingeplant
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ApprovedPendingList
                posts={data!.approvedPending!}
                onOpen={(p) => setLocation(postModuleHref({ id: p.id, platform: p.platform, mediaFormat: p.mediaFormat }, edition))}
              />
            </CardContent>
          </Card>
        </AnimatedSection>
      )}

      {/* Upcoming Scheduled Posts */}
      <AnimatedSection delay={200}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarClock className="h-5 w-5 text-blue-600" />
              Geplante Posts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {upcoming.length === 0 ? (
              <div className="text-center py-8">
                <CalendarClock className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">
                  Keine geplanten Posts.
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Plane Posts über die Content-Bereiche in der Seitenleiste.
                </p>
              </div>
            ) : (
              <DayGroups groups={upcomingByDay} order="asc" kind="scheduled" highlightId={highlightId} onOpen={openPost} />
            )}
            {edition?.kind === "hotel" && upcoming.length > 0 && (
              <p className="text-xs text-muted-foreground mt-3" data-testid="hint-planner-delete">
                Bereits eingeplante Beiträge ändern oder löschen Sie bitte im Planer oder über Ihr Team.
              </p>
            )}
          </CardContent>
        </Card>
      </AnimatedSection>

      {/* Recently Published Posts */}
      <AnimatedSection delay={300}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-600" />
              Veröffentlichte Posts
            </CardTitle>
          </CardHeader>
          <CardContent>
            {posted.length === 0 ? (
              <div className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">
                  Keine veröffentlichten Posts {rangeHint.toLowerCase()}.
                </p>
              </div>
            ) : (
              <DayGroups groups={postedByDay} order="desc" kind="posted" highlightId={highlightId} onOpen={openPost} />
            )}
          </CardContent>
        </Card>
      </AnimatedSection>
    </div>
  )
}
